import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Printer, Share2, ShieldAlert, Loader2, ReceiptText, Heart } from "lucide-react";
import { api, fmtPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FUL = { new: "جديد", preparing: "قيد التجهيز", ready: "جاهز للتسليم", delivering: "قيد التوصيل", delivered: "تم التسليم", cancelled: "ملغي" };

export default function Invoice() {
  const { token } = useParams();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | ok | invalid | forbidden
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!ready) return;
    if (!user) { navigate(`/login?redirect=/invoice/${token}`); return; }
    api.post("/orders/verify", { token })
      .then((r) => { setData(r.data); setState("ok"); })
      .catch((e) => setState(e.response?.status === 403 ? "forbidden" : "invalid"));
  }, [ready, user, token]);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("تم نسخ رابط الفاتورة");
    } catch { toast.error("تعذّر النسخ"); }
  };

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center pt-24">
        <Loader2 className="h-10 w-10 animate-spin text-olive" />
      </div>
    );
  }

  if (state === "invalid" || state === "forbidden") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 px-6 pt-24 text-center">
        <ShieldAlert className="h-16 w-16 text-palred/60" />
        <h1 className="font-display text-2xl font-bold text-ink">
          {state === "forbidden" ? "هذا الطلب لا يتبع حسابك" : "رمز QR غير صالح أو منتهٍ"}
        </h1>
        <p className="max-w-md text-ink/55">
          {state === "forbidden" ? "حمايةً لخصوصية العملاء، لا يمكن عرض فواتير طلبات الآخرين." : "تأكد من مسح الرمز المطبوع على الطرد، أو تواصل معنا."}
        </p>
        <Link to="/account" className="rounded-full bg-olive px-7 py-3 font-semibold text-ivory">حسابي</Link>
      </div>
    );
  }

  const o = data.order;
  const addr = [o.address, o.building && `بناء ${o.building}`, o.street, o.area, o.city, o.country].filter(Boolean).join("، ");

  return (
    <div className="min-h-screen bg-sand/30 pb-16 pt-24">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <div data-testid="invoice-card" className="overflow-hidden rounded-[1.75rem] border border-olive/10 bg-white shadow-xl">
          {/* header */}
          <div className="flex items-center justify-between bg-olive-deep px-6 py-5 text-ivory sm:px-8">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="سهول جنين" className="h-12 w-12 rounded-xl object-cover" />
              <div>
                <div className="font-display text-lg font-bold">سهول جنين</div>
                <div className="text-[11px] text-ivory/70">جودة من أرضنا 🇵🇸</div>
              </div>
            </div>
            <div className="text-left">
              <div className="flex items-center gap-1.5 text-xs text-ivory/70"><ReceiptText className="h-3.5 w-3.5" /> فاتورة إلكترونية</div>
              <div className="mt-1 font-display font-bold">{o.order_no}</div>
            </div>
          </div>

          <div className="px-6 py-6 sm:px-8">
            <h1 className="font-display text-xl font-bold text-ink">فاتورتك من سهول جنين</h1>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <Meta label="التاريخ" value={new Date(o.created_at).toLocaleString("ar")} />
              <Meta label="الحالة" value={FUL[o.fulfillment_status] || "جديد"} badge />
              <Meta label="العميل" value={o.name} />
              <Meta label="الهاتف" value={o.phone} ltr />
              <div className="col-span-2"><Meta label="العنوان" value={addr} /></div>
              {o.note && <div className="col-span-2"><Meta label="ملاحظات" value={o.note} /></div>}
            </div>

            {/* items */}
            <div className="mt-6 overflow-hidden rounded-2xl border border-olive/10">
              <div className="grid grid-cols-[1fr_auto] gap-2 bg-olive/5 px-4 py-2.5 text-xs font-semibold text-ink/55 sm:grid-cols-[1fr_70px_90px_90px]">
                <span>المنتج</span><span className="hidden text-center sm:block">الكمية</span><span className="hidden text-center sm:block">سعر الوحدة</span><span className="text-left">المجموع</span>
              </div>
              {o.items.map((it) => (
                <div key={it.sku} className="grid grid-cols-[1fr_auto] items-center gap-2 border-t border-olive/8 px-4 py-3 text-sm sm:grid-cols-[1fr_70px_90px_90px]">
                  <span className="font-medium text-ink">{it.name_ar}</span>
                  <span className="hidden text-center text-ink/60 sm:block">×{it.qty}</span>
                  <span className="hidden text-center text-ink/60 sm:block">{fmtPrice(it.price)}</span>
                  <span className="text-left font-semibold text-olive-dark">{fmtPrice(it.price * it.qty)}</span>
                </div>
              ))}
            </div>

            {/* totals */}
            <div className="mt-5 space-y-1.5 text-sm">
              <Tot label="الإجمالي الفرعي" value={fmtPrice(o.subtotal)} />
              <Tot label="الشحن" value={fmtPrice(o.shipping)} />
              {o.discount > 0 && <Tot label="الخصم" value={`- ${fmtPrice(o.discount)}`} />}
              <div className="my-2 border-t border-olive/10" />
              <Tot label="الإجمالي النهائي" value={fmtPrice(o.total)} bold />
            </div>

            {/* custom fields */}
            {data.fields?.filter((f) => o.custom_fields?.[f.key] !== undefined && o.custom_fields?.[f.key] !== "").length > 0 && (
              <div className="mt-5 grid grid-cols-2 gap-3">
                {data.fields.filter((f) => o.custom_fields?.[f.key] !== undefined && o.custom_fields?.[f.key] !== "").map((f) => (
                  <Meta key={f.key} label={f.label} value={o.custom_fields[f.key] === true ? "نعم" : String(o.custom_fields[f.key])} />
                ))}
              </div>
            )}

            {/* thank you */}
            <div className="mt-7 rounded-2xl bg-olive/[0.06] p-6 text-center">
              <Heart className="mx-auto h-6 w-6 fill-palred/80 text-palred/80" />
              <div className="mt-2 font-display text-lg font-bold text-olive-dark">شكرًا لثقتك بـ سهول جنين</div>
              <p className="mx-auto mt-2 max-w-md whitespace-pre-line text-sm leading-relaxed text-ink/65">{data.thank_you}</p>
            </div>
          </div>
        </div>

        <div className="no-print mt-6 flex flex-wrap justify-center gap-3">
          <button data-testid="invoice-print" onClick={() => window.print()}
            className="flex items-center gap-2 rounded-full bg-olive px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-olive-dark">
            <Printer className="h-4 w-4" /> طباعة / تصدير PDF
          </button>
          <button data-testid="invoice-share" onClick={share}
            className="flex items-center gap-2 rounded-full border border-olive/25 px-6 py-3 text-sm font-semibold text-olive-dark transition-colors hover:bg-olive/5">
            <Share2 className="h-4 w-4" /> مشاركة
          </button>
          <Link to="/account" className="flex items-center rounded-full border border-olive/25 px-6 py-3 text-sm font-semibold text-olive-dark">حسابي</Link>
        </div>
      </div>
    </div>
  );
}

const Meta = ({ label, value, ltr, badge }) => (
  <div className="rounded-xl bg-olive/[0.04] px-3.5 py-2.5">
    <div className="text-[11px] text-ink/45">{label}</div>
    {badge ? (
      <span className="mt-0.5 inline-block rounded-full bg-olive/10 px-2.5 py-0.5 text-xs font-semibold text-olive">{value}</span>
    ) : (
      <div className={`mt-0.5 font-medium text-ink ${ltr ? "text-left" : ""}`} dir={ltr ? "ltr" : "rtl"}>{value}</div>
    )}
  </div>
);

const Tot = ({ label, value, bold }) => (
  <div className={`flex items-center justify-between ${bold ? "font-display text-lg font-bold text-olive-dark" : "text-ink/65"}`}>
    <span>{label}</span><span>{value}</span>
  </div>
);
